import { Observer } from "../animation/Animatable/Observers"
import { Cancel } from "../animation/Animatable"
import { ObservableObject } from "./ObservableObject"

/**
 * Allows data to be shared between Frames using Code Overrides.
 * Any changes to the `Data` instance will cause the preview to update and code
 * overrides will re-render. In this example, we’re updating the `scale` property on `press`, setting it to `0.5`.
 * ```jsx
 * import { Data, Override } from "framer"
 *
 * const data = Data({
 *    scale: 0.5,
 * })
 *
 * export function WhileTap(): Override {
 *    return {
 *        whileTap: {
 *            scale: data.scale,
 *        },
 *    }
 * }
 *
 * ```
 * @param initial - The initial value of the data to be set.
 * @returns the data object for use across components.
 * @public
 */
export function Data<T extends object = object>(initial: Partial<T> | object = {}): T {
    // Because of the second boolean is set to false we already know that everything will have the same type as the input
    const data: T = ObservableObject(initial, false, false) as any
    Data.addData(data)
    return data
}

/**
 * @public
 * @internalremarks The release tag on this should be internal, but API extractor does not support that yet: https://github.com/Microsoft/web-build-tools/issues/972
 */
export namespace Data {
    /**
     * @internal
     */
    export let _stores: object[] = []
    /** @internal */
    export function addData(data: object) {
        _stores.push(data)
    }
    /** @internal */
    export function addObserver<T extends object>(target: T, observer: Observer<T>): Cancel {
        return ObservableObject.addObserver(target, observer)
    }
}
